import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, TrendingDown, Wallet, CalendarDays, Sparkles, Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Seo } from '@/components/Seo';
import { FaqSection } from '@/components/FaqSection';

const ROI_FAQS = [
  {
    q: 'Wie funktioniert der ROI-Rechner?',
    a: 'Der Rechner vergleicht deine klassischen CPI-Kosten (inklusive Streuverlust und Fraud) mit einem PPI-Fixpreis pro verifiziertem Install. Formel: Klassische Kosten = Installs × CPI ÷ (1 − Streuverlust), PPI-Kosten = Installs × PPI-Preis. Die Differenz ist dein Einsparpotenzial pro Monat und Jahr.'
  },
  {
    q: 'Was ist der Unterschied zwischen CPI und PPI?',
    a: 'Beim CPI (Cost per Install) zahlst du für Installs aus Klick-Kampagnen – inklusive ungültiger Klicks, Bots und nicht attribuierbarer Installs (branchenüblich 15–30% des Budgets). Beim PPI (Pay-per-Install) zahlst du einen Fixpreis ausschliesslich für verifizierte App-Downloads.'
  },
  {
    q: 'Wie erhalte ich meinen ROI-Report als PDF?',
    a: 'Gib deine E-Mail-Adresse im Formular unter den Ergebnissen ein. Du erhältst den persönlichen PDF-Report kostenlos per E-Mail – ohne Newsletter-Anmeldung und ohne Kreditkarte.'
  },
  {
    q: 'Sind die berechneten Einsparungen garantiert?',
    a: 'Nein. Die Berechnung ist eine Modellrechnung auf Basis deiner Eingaben und branchenüblicher Erfahrungswerte. Tatsächliche Ergebnisse hängen von App, Zielgruppe und Marktumfeld ab.'
  },
];

const fmtCHF = (v) => `CHF ${Math.round(v).toLocaleString('de-CH')}`;

export default function ROIRechnerPage() {
  const [installs, setInstalls] = useState(10000);
  const [cpi, setCpi] = useState(6.5);
  const [ppi, setPpi] = useState(4.9);
  const [waste, setWaste] = useState(20);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const sendReport = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/roi/lead`, {
        email, installs, cpi, ppi, waste_pct: waste,
      });
      if (res.data.status === 'sent') {
        toast.success('Dein ROI-Report ist unterwegs! Prüfe dein Postfach. 📩');
      } else {
        toast.success('Anfrage gespeichert – wir melden uns bei dir!');
      }
      setEmail('');
    } catch {
      toast.error('Senden fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setSending(false);
    }
  };

  const r = useMemo(() => {
    const classicCost = (installs * cpi) / (1 - waste / 100);
    const ppiCost = installs * ppi;
    const savingMonthly = classicCost - ppiCost;
    const savingPct = classicCost > 0 ? (savingMonthly / classicCost) * 100 : 0;
    return { classicCost, ppiCost, savingMonthly, savingPct, savingYearly: savingMonthly * 12 };
  }, [installs, cpi, ppi, waste]);

  const positive = r.savingMonthly > 0;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Seo
        title="ROI-Rechner: Einsparpotenzial mit Pay-per-Install berechnen"
        description="Berechne in 30 Sekunden, wie viel Budget du mit Pay-per-Install (PPI) gegenüber klassischen CPI-Kampagnen sparst. Kostenloser interaktiver ROI-Rechner."
        path="/roi-rechner"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "marKIpilot ROI-Rechner",
          "url": "https://markipilot.com/roi-rechner",
          "applicationCategory": "BusinessApplication",
          "inLanguage": "de-CH",
          "description": "Interaktiver Rechner zum Einsparpotenzial von Pay-per-Install gegenüber klassischen CPI-Kampagnen.",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "CHF" },
          "publisher": { "@type": "Organization", "name": "MYSYMP AG" }
        }}
      />

      <nav className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-teal-400 transition-colors" data-testid="roi-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Zurück</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">✦</span>
            </div>
            <span className="font-bold text-lg">mar<span className="text-teal-400">KI</span>pilot</span>
          </Link>
        </div>
      </nav>

      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-sm mb-6">
              <Sparkles className="w-4 h-4" /> Kostenloser ROI-Rechner
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Dein Einsparpotenzial mit <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">Pay-per-Install</span>
            </h1>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mb-12">
              Klassische CPI-Kampagnen bezahlen Klicks, Streuverlust und Fraud mit. Mit PPI zahlst du nur für verifizierte Installs. Rechne selbst nach:
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Inputs */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-8" data-testid="roi-inputs">
              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-sm text-slate-300">Monatliche Install-Ziele</label>
                  <span className="text-teal-400 font-bold" data-testid="roi-installs-value">{installs.toLocaleString('de-CH')}</span>
                </div>
                <Slider value={[installs]} onValueChange={([v]) => setInstalls(v)} min={1000} max={100000} step={1000} data-testid="roi-installs-slider" />
              </div>
              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-sm text-slate-300">Dein aktueller CPI (klassische Kampagnen)</label>
                  <span className="text-teal-400 font-bold" data-testid="roi-cpi-value">CHF {cpi.toFixed(2)}</span>
                </div>
                <Slider value={[cpi]} onValueChange={([v]) => setCpi(v)} min={1} max={20} step={0.1} data-testid="roi-cpi-slider" />
              </div>
              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-sm text-slate-300">PPI-Fixpreis pro verifiziertem Install</label>
                  <span className="text-teal-400 font-bold" data-testid="roi-ppi-value">CHF {ppi.toFixed(2)}</span>
                </div>
                <Slider value={[ppi]} onValueChange={([v]) => setPpi(v)} min={1} max={20} step={0.1} data-testid="roi-ppi-slider" />
              </div>
              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-sm text-slate-300">Streuverlust & Fraud bei klassischem Einkauf</label>
                  <span className="text-teal-400 font-bold" data-testid="roi-waste-value">{waste}%</span>
                </div>
                <Slider value={[waste]} onValueChange={([v]) => setWaste(v)} min={0} max={50} step={1} data-testid="roi-waste-slider" />
                <p className="text-xs text-slate-500 mt-2">Branchenüblich: 15–30% des Budgets versickern in ungültigen Klicks, Bots und nicht attribuierbaren Installs.</p>
              </div>
            </div>

            {/* Spotlight Results: 3 glasklare Zahlen */}
            <div className="space-y-4">
              <motion.div
                key={Math.round(r.savingMonthly)}
                initial={{ scale: 0.97, opacity: 0.6 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`rounded-2xl p-8 border ${positive ? 'bg-gradient-to-br from-teal-500/20 to-cyan-500/10 border-teal-500/30' : 'bg-white/5 border-white/10'}`}
                data-testid="roi-result-monthly"
              >
                <div className="flex items-center gap-2 text-teal-400 text-sm mb-2"><Wallet className="w-4 h-4" /> Monatliche Einsparung</div>
                <div className="text-4xl sm:text-5xl font-bold">{fmtCHF(Math.max(r.savingMonthly, 0))}</div>
              </motion.div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="roi-result-pct">
                  <div className="flex items-center gap-2 text-teal-400 text-sm mb-2"><TrendingDown className="w-4 h-4" /> Budget-Reduktion</div>
                  <div className="text-3xl font-bold">{Math.max(r.savingPct, 0).toFixed(0)}%</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="roi-result-yearly">
                  <div className="flex items-center gap-2 text-teal-400 text-sm mb-2"><CalendarDays className="w-4 h-4" /> Pro Jahr</div>
                  <div className="text-3xl font-bold">{fmtCHF(Math.max(r.savingYearly, 0))}</div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-sm" data-testid="roi-comparison">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-slate-400">Klassisch (CPI inkl. Streuverlust)</span>
                  <span className="font-semibold">{fmtCHF(r.classicCost)}/Monat</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-400">Pay-per-Install (nur verifizierte Installs)</span>
                  <span className="font-semibold text-teal-400">{fmtCHF(r.ppiCost)}/Monat</span>
                </div>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <a href="mailto:info@markipilot.com?subject=ROI-Analyse%20Pay-per-Install" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white rounded-full h-12" data-testid="roi-cta-contact">
                    Kostenlose ROI-Analyse anfordern <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Link to="/was-ist-pay-per-install" className="flex-1">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 rounded-full h-12 bg-transparent" data-testid="roi-cta-ppi-info">
                    Was ist PPI?
                  </Button>
                </Link>
              </div>

              <form onSubmit={sendReport} className="bg-gradient-to-br from-teal-500/10 to-cyan-500/5 border border-teal-500/20 rounded-2xl p-6" data-testid="roi-lead-form">
                <div className="flex items-center gap-2 text-teal-400 text-sm font-medium mb-3">
                  <Mail className="w-4 h-4" /> Ergebnis als PDF-Report per E-Mail erhalten
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="deine@email.ch"
                    className="flex-1 bg-white/5 border-white/20 text-white placeholder:text-slate-500 h-11 rounded-full px-5"
                    data-testid="roi-lead-email-input"
                  />
                  <Button type="submit" disabled={sending} className="bg-teal-500 hover:bg-teal-400 text-white rounded-full h-11 px-6" data-testid="roi-lead-submit-button">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Report senden'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-3">Kostenlos & unverbindlich. Wir nutzen deine E-Mail nur für den Report und eine mögliche Kontaktaufnahme. Kein Newsletter-Abo.</p>
              </form>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-10 max-w-3xl" data-testid="roi-disclaimer">
            Hinweis: Diese Berechnung ist eine Modellrechnung auf Basis deiner Eingaben und branchenüblicher Erfahrungswerte. Tatsächliche Ergebnisse hängen von App, Zielgruppe und Marktumfeld ab.
          </p>
        </div>
      </section>

      <FaqSection faqs={ROI_FAQS} title="Häufige Fragen zum ROI-Rechner" dark />
    </div>
  );
}
